import { TasksResponseType } from '@/api/backend/types';
import { ResponsiveDataView } from '@/components/responsive-data-view';
import { Card, DataTable, DataTableColumnType, Link, SearchInput } from '@opaz/ui-components';

import { getTasks } from '@/api/backend/workflow';
import { TaskStatusBadge } from '@/components/task-status-badge';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

const PAGE_SIZE = 10;

export function AllTasksCard() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [currentPage, setCurrentPage] = useState(0);
  const [tempSearchValue, setTempSearchValue] = useState<string>('');
  const [searchValue, setSearchValue] = useState<string>('');

  const { data: page, isLoading: isTasksLoading } = useQuery({
    queryKey: ['tasks', currentPage, searchValue],
    queryFn: () => {
      return getTasks(
        {
          start: currentPage * PAGE_SIZE,
          size: PAGE_SIZE,
        },
        { state: 'created', searchTerm: searchValue || undefined },
      );
    },
  });

  const columns: DataTableColumnType<TasksResponseType>[] = [
    {
      title: t('dashboard.tasks.headings.id'),
      render: (record: TasksResponseType) => {
        let taskName = record.id;
        const match = record.processInstanceName?.match(/^APP-\d{7}/);

        if (match && match[0]) {
          taskName = match[0];
        }

        return (
          <Link
            onClick={() => {
              navigate({
                to: `/processes/$processInstanceId/task/$taskId`,
                params: { processInstanceId: record.processInstanceId, taskId: record.id },
                search: {
                  responseToken: undefined,
                },
              });
            }}
            className="text-start w-[120px]"
          >
            {taskName}
          </Link>
        );
      },
    },
    {
      title: t('dashboard.tasks.headings.task'),
      render: (record: TasksResponseType) => (
        <span className="font-bold text-[#113561]">{record.translations?.[i18n.language]?.name || record.name}</span>
      ),
    },
    {
      title: t('dashboard.tasks.headings.application'),
      render: (record: TasksResponseType) => (
        <>
          <Link
            onClick={() => {
              navigate({
                to: `/processes/$processInstanceId`,
                params: { processInstanceId: record.processInstanceId },
                search: { from: undefined, version: undefined },
              });
            }}
            className="font-bold text-start"
          >
            {record.translations?.[i18n.language]?.parentScopeDefinitionName || record.processDefinitionName}
          </Link>
          <br />
        </>
      ),
    },
    {
      title: t('dashboard.tasks.headings.zone'),
      align: 'end',
      render: () => t('common.unknown'),
    },
    {
      title: t('dashboard.tasks.headings.dueDate'),
      align: 'end',
      render: (record: TasksResponseType) => {
        return <TaskStatusBadge endTime={record.endTime} dueDate={record.dueDate} />;
      },
    },
  ];

  return (
    <Card className="w-full p-5 shadow-lg border-none">
      <h2 className="text-2xl font-semibold mb-6">{t('dashboard.tasks.title')}</h2>

      <SearchInput
        className="mb-5 max-w-md"
        onChange={(e) => {
          setTempSearchValue(e.target.value);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            setSearchValue(tempSearchValue);
            setCurrentPage(0);
          }
        }}
        value={tempSearchValue || ''}
        onClear={() => {
          setTempSearchValue('');
          setSearchValue('');
          setCurrentPage(0);
        }}
        onSearchSubmit={() => {
          setSearchValue(tempSearchValue);
          setCurrentPage(0);
        }}
      />

      <ResponsiveDataView
        columns={columns}
        data={page?.data?.length ? [...page.data] : []}
        isLoading={isTasksLoading}
        emptyMessage={t('common.table.emptyState')}
        currentPage={currentPage + 1}
        totalPages={page?.total ? Math.ceil(page.total / PAGE_SIZE) : 0}
        onPageChange={(page) => setCurrentPage(page - 1)}
        getRowKey={(record) => record.id}
      >
        <DataTable
          columns={columns}
          dataSource={page?.data?.length ? [...page.data] : []}
          isLoading={isTasksLoading}
          currentPage={currentPage + 1}
          totalPages={page?.total ? Math.ceil(page.total / PAGE_SIZE) : 0}
          onPageChange={(page) => setCurrentPage(page - 1)}
          translations={{
            apply: t('common.table.filter.apply'),
            clear: t('common.table.filter.clear'),
            from: t('common.table.filter.from'),
            clearAllFilters: t('common.table.filter.clearAllFilters'),
            noData: t('common.table.emptyState'),
            nextPage: t('common.table.next'),
            previousPage: t('common.table.previous'),
          }}
        />
      </ResponsiveDataView>
    </Card>
  );
}
